import type { Workflow } from '@n8n-builder/n8n-types';
import type { DebugLog } from '@n8n-builder/api-client';
import type { WorkflowPlan } from '../../store/chat';
import { XMarkIcon, CubeIcon, DocumentIcon, KeyIcon, FlagIcon } from '../ui/icons';
import { useUIStore, type SidePanelTab } from '../../store/ui';
import { WorkflowPreview } from './WorkflowPreview';
import { PlanPreview } from './PlanPreview';
import { ReportIssuePanel } from './ReportIssuePanel';
import { CredentialsPanel } from './CredentialsPanel';

interface SidePanelProps {
  workflow: Workflow | null;
  workflowId: string | null;
  plan: WorkflowPlan | null;
  debugLogs: DebugLog[];
  onDeploy: (workflow: Workflow, workflowId?: string) => void;
  onBuild: () => void;
  isDeploying: boolean;
  isBuilding: boolean;
}

export function SidePanel({
  workflow,
  workflowId,
  plan,
  debugLogs,
  onDeploy,
  onBuild,
  isDeploying,
  isBuilding,
}: SidePanelProps) {
  const { closeSidePanel, activeSidePanelTab, setActiveSidePanelTab } = useUIStore();

  const tabs: { id: SidePanelTab; label: string }[] = [
    { id: 'plan', label: 'Plan' },
    { id: 'workflow', label: 'Workflow' },
    { id: 'credentials', label: 'Credentials' },
    { id: 'report', label: 'Report Issue' },
  ];

  // Determine which tab to show based on what content is available
  const hasPlan = !!plan;
  const hasWorkflow = !!workflow;
  const hasCredentials = hasWorkflow;

  return (
    <div className="flex h-full flex-col min-h-0">
      {/* Panel Header with Tabs */}
      <div className="flex-shrink-0 flex items-center justify-between border-b border-gray-200 px-4 py-2">
        {/* Tab Navigation */}
        <div className="flex gap-1 flex-1 justify-center">
          {tabs.map((tab) => {
            const isActive = activeSidePanelTab === tab.id;
            const isReport = tab.id === 'report';
            const hasContent =
              tab.id === 'plan' ? hasPlan :
              tab.id === 'workflow' ? hasWorkflow :
              tab.id === 'credentials' ? hasCredentials :
              false;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveSidePanelTab(tab.id)}
                className={`relative flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? isReport
                      ? 'text-red-600 bg-red-50'
                      : 'text-primary-700 bg-primary-50'
                    : isReport
                      ? 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {isReport && <FlagIcon className="h-3.5 w-3.5" />}
                {tab.label}
                {/* Indicator dot for content */}
                {hasContent && !isActive && (
                  <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary-400" />
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={closeSidePanel}
          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      {activeSidePanelTab === 'plan' && hasPlan ? (
        <PlanPreview
          plan={plan}
          onBuild={onBuild}
          isBuilding={isBuilding}
          isBuilt={hasWorkflow}
        />
      ) : activeSidePanelTab === 'workflow' && hasWorkflow ? (
        <WorkflowPreview
          workflow={workflow}
          workflowId={workflowId}
          onDeploy={onDeploy}
          isDeploying={isDeploying}
        />
      ) : activeSidePanelTab === 'credentials' && hasWorkflow ? (
        <CredentialsPanel workflow={workflow} />
      ) : activeSidePanelTab === 'report' ? (
        <ReportIssuePanel logs={debugLogs} />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100">
            {activeSidePanelTab === 'plan' ? (
              <DocumentIcon className="h-6 w-6 text-gray-400" />
            ) : activeSidePanelTab === 'credentials' ? (
              <KeyIcon className="h-6 w-6 text-gray-400" />
            ) : (
              <CubeIcon className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <h3 className="text-sm font-medium text-gray-900">
            {activeSidePanelTab === 'plan' ? 'No plan yet' :
             activeSidePanelTab === 'credentials' ? 'No workflow yet' :
             'No workflow yet'}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            {activeSidePanelTab === 'plan'
              ? 'Describe your automation to generate a plan'
              : activeSidePanelTab === 'credentials'
              ? 'Build a workflow to configure credentials'
              : 'Approve a plan to build a workflow'}
          </p>
        </div>
      )}
    </div>
  );
}
